import { useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, Upload } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function InsuranceForm() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(!user?.decennale_company);
  const [form, setForm] = useState({
    decennale_company: user?.decennale_company || "",
    decennale_policy_number: user?.decennale_policy_number || "",
    decennale_valid_until: user?.decennale_valid_until || "",
    rcpro_company: user?.rcpro_company || "",
    rcpro_policy_number: user?.rcpro_policy_number || "",
    rcpro_valid_until: user?.rcpro_valid_until || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.patch("/users/me", form);
      toast.success("Assurances enregistrées ✅");
      try { await refresh?.(); } catch (_e) { /* ignore */ }
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const uploadAttestation = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await apiClient.post("/users/me/insurance-attestation", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attestation ${kind === "decennale" ? "décennale" : "RC Pro"} déposée ✅`);
      try { await refresh?.(); } catch (_e) { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading("");
    }
  };

  const hasDecennale = !!user?.decennale_company;
  const frDate = (d) => (d ? d.split("-").reverse().join("/") : "");

  if (!open && hasDecennale) {
    return (
      <div
        className="mb-8 border-2 border-blue-700 bg-blue-50 p-4 flex flex-wrap items-center gap-3"
        data-testid="insurance-summary"
      >
        <CheckCircle2 className="w-6 h-6 text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="display text-lg text-blue-900">Assurances déclarées</div>
          <div className="mono text-[10px] uppercase tracking-widest text-blue-800 mt-0.5">
            Décennale : {user.decennale_company}
            {user.decennale_valid_until && ` · valide jusqu'au ${frDate(user.decennale_valid_until)}`}
            {user.rcpro_company && ` — RC Pro : ${user.rcpro_company}`}
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="mono text-[10px] uppercase tracking-widest border border-blue-700 text-blue-900 px-3 py-1.5 hover:bg-blue-700 hover:text-white transition"
          data-testid="insurance-edit"
        >
          Modifier
        </button>
      </div>
    );
  }

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="insurance-form">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-[#FF4D00]" />
        <div>
          <div className="display text-xl">Vos assurances professionnelles</div>
          <div className="text-xs text-zinc-600">
            RC Pro et garantie décennale — affichées sur votre profil, elles rassurent les demandeurs et débloquent plus de chantiers.
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-6">
        <div className="border-2 border-zinc-200 p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Garantie décennale</div>
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">Assureur</label>
          <input value={form.decennale_company} onChange={set("decennale_company")} placeholder="Ex: MAAF, SMABTP, AXA…"
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="decennale-company" />
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">N° de police</label>
          <input value={form.decennale_policy_number} onChange={set("decennale_policy_number")}
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="decennale-policy" />
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">Valide jusqu'au</label>
          <input type="date" value={form.decennale_valid_until} onChange={set("decennale_valid_until")}
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="decennale-valid-until" />
          <label className="mt-4 flex items-center gap-2 cursor-pointer border-2 border-dashed border-zinc-300 hover:border-[#FF4D00] p-3 text-xs text-zinc-600">
            {uploading === "decennale" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {user?.decennale_attestation ? "Attestation déposée — remplacer" : "Joindre l'attestation (PDF/JPG/PNG)"}
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
              onChange={(e) => uploadAttestation("decennale", e.target.files?.[0])} data-testid="decennale-attestation-input" />
          </label>
        </div>
        <div className="border-2 border-zinc-200 p-4">
          <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">// RC Professionnelle</div>
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">Assureur</label>
          <input value={form.rcpro_company} onChange={set("rcpro_company")} placeholder="Ex: MAAF, Groupama…"
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="rcpro-company" />
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">N° de police</label>
          <input value={form.rcpro_policy_number} onChange={set("rcpro_policy_number")}
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="rcpro-policy" />
          <label className="block mt-3 mono text-[10px] uppercase tracking-widest text-zinc-600">Valide jusqu'au</label>
          <input type="date" value={form.rcpro_valid_until} onChange={set("rcpro_valid_until")}
            className="w-full mt-1 border-2 border-[#0A0A0B] px-3 py-2 text-sm" data-testid="rcpro-valid-until" />
          <label className="mt-4 flex items-center gap-2 cursor-pointer border-2 border-dashed border-zinc-300 hover:border-[#FF4D00] p-3 text-xs text-zinc-600">
            {uploading === "rcpro" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {user?.rcpro_attestation ? "Attestation déposée — remplacer" : "Joindre l'attestation (PDF/JPG/PNG)"}
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
              onChange={(e) => uploadAttestation("rcpro", e.target.files?.[0])} data-testid="rcpro-attestation-input" />
          </label>
        </div>
        <div className="md:col-span-2 flex items-center justify-between gap-3">
          <p className="text-[11px] text-zinc-500">
            La garantie décennale est obligatoire pour tous les travaux de construction (loi Spinetta, art. 1792 Code civil).
          </p>
          <button type="submit" disabled={busy} className="btn-batisur text-sm disabled:opacity-60" data-testid="insurance-submit">
            {busy ? "Enregistrement…" : "Enregistrer mes assurances"}
          </button>
        </div>
      </form>
    </div>
  );
}
